/**
 * BRSR Module Component
 * 
 * Business Responsibility & Sustainability Reporting module.
 * Tabs:
 * - Section A: General Disclosures (Company details, products, operations)
 * - Section B: Management & Process Disclosures
 * - Section C: Principle-wise Performance Disclosures (P1-P9)
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { 
  BarChart3,
  Pencil,
  X,
  ClipboardList,
  Building2
} from 'lucide-react';
import { Button } from './ui/button';
import { generateReportingYears, getCurrentReportingYear } from '../utils/reportingYearUtils';
import ESGQuestionnaire from './ESGQuestionnaire';
import BRSRSectionC from './BRSRSectionC';
import BRSRDetailsSection from './BRSRDetailsSection';
import { BRSRExportButton } from '../modules/brsr-export';
import { ModulePageHeader } from './ModulePageHeader';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BRSRModule() {
  const { token } = useAuth();
  
  const [activeTab, setActiveTab] = useState('section_a');
  const [reportingPeriod, setReportingPeriod] = useState('');
  const [reportingYears, setReportingYears] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [responseRefreshKey, setResponseRefreshKey] = useState(0);
  const [liveKPIAutofill, setLiveKPIAutofill] = useState({});

  // Initialize reporting years
  useEffect(() => {
    const fetchOrgAndSetYears = async () => {
      try {
        const res = await axios.get(`${API}/api/organizations/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const yearType = res.data.reporting_year_type || 'financial_year';
        const years = generateReportingYears(yearType, 5);
        setReportingYears(years);
        setReportingPeriod(getCurrentReportingYear(yearType));
        setOrganization(res.data);
      } catch (error) {
        const years = generateReportingYears('financial_year', 5);
        setReportingYears(years);
        setReportingPeriod(getCurrentReportingYear('financial_year'));
      }
    };
    
    if (token) {
      fetchOrgAndSetYears();
    }
  }, [token]);

  useEffect(() => {
    let active = true;
    const loadLiveKPIAutofill = async () => {
      if (!token || !reportingPeriod) return;
      try {
        const response = await axios.get(`${API}/api/brsr-report/live-kpi-autofill`, {
          params: { reporting_period: reportingPeriod },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setLiveKPIAutofill(response.data.values_by_question || {});
      } catch (error) {
        console.error('Failed to sync live BRSR KPI values:', error);
      }
    };
    loadLiveKPIAutofill();
    const interval = window.setInterval(loadLiveKPIAutofill, 30000);
    const refreshOnFocus = () => loadLiveKPIAutofill();
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [token, reportingPeriod, responseRefreshKey]);

  return (
    <div className="space-y-7">
      <ModulePageHeader
        title="BRSR Framework"
        icon={ClipboardList}
        iconClassName="border-emerald-200 bg-emerald-50 text-emerald-800"
        testId="brsr-framework"
        aside={<>
          <Label className="text-sm whitespace-nowrap">Reporting Period:</Label>
          <Select value={reportingPeriod} onValueChange={setReportingPeriod}>
            <SelectTrigger className="w-40 bg-white" data-testid="brsr-reporting-period-selector">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              {reportingYears.map(year => (
                <SelectItem key={year} value={year} data-testid={`brsr-reporting-period-option-${year}`}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <BRSRExportButton 
            reportingPeriod={reportingPeriod} 
            organization={organization}
          />
          <Button
            variant={isEditing ? 'outline' : 'default'}
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
            className={isEditing ? 'border-red-300 text-red-600 hover:bg-red-50' : ''}
            data-testid="brsr-edit-toggle-button">
            {isEditing ? <><X className="w-4 h-4 mr-1" /> Done</> : <><Pencil className="w-4 h-4 mr-1" /> Edit</>}
          </Button>
        </>}
      />

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 p-1 rounded-lg">
          <TabsTrigger value="section_a" className="gap-2" data-testid="brsr-section-a-tab">
            <Building2 className="w-4 h-4" />
            Section A
          </TabsTrigger>
          <TabsTrigger value="section_b" className="gap-2" data-testid="brsr-section-b-tab">
            <ClipboardList className="w-4 h-4" />
            Section B
          </TabsTrigger>
          <TabsTrigger value="section_c" className="gap-2" data-testid="brsr-section-c-tab">
            <BarChart3 className="w-4 h-4" />
            Section C
          </TabsTrigger>
        </TabsList>

        {/* Section A - General Disclosures */}
        <TabsContent value="section_a" className="mt-6">
          <BRSRDetailsSection
            key={`section-a-${responseRefreshKey}`}
            isEditing={isEditing}
            isCollapsible={false}
            reportingPeriod={reportingPeriod}
            liveKPIAutofillByQuestion={liveKPIAutofill}
          />
        </TabsContent>

        {/* Section B - Management & Process Disclosures */}
        <TabsContent value="section_b" className="mt-6">
          <ESGQuestionnaire
            key={`section-b-${responseRefreshKey}`}
            framework="BRSR"
            section="section_b"
            isEditing={isEditing}
            reportingYear={reportingPeriod}
            liveKPIAutofillByQuestion={liveKPIAutofill}
          />
        </TabsContent>

        {/* Section C - Principle-wise Performance Disclosures */}
        <TabsContent value="section_c" className="mt-6">
          <BRSRSectionC
            key={`section-c-${responseRefreshKey}`}
            framework="BRSR"
            isEditing={isEditing}
            reportingYear={reportingPeriod}
            liveKPIAutofillByQuestion={liveKPIAutofill}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
